<template>
  <div class="mobile-home">
    <!-- 顶部标题栏 -->
    <MobileHeader 
      :title="title"
      @showSettings="handleShowSettings" 
    />
    
    <!-- 搜索栏和创建按钮 -->
    <div class="search-create-section">
      <div class="search-wrapper">
        <i class="search-icon">🔍</i>
        <input 
          v-model="searchQuery"
          type="text" 
          placeholder="Search my notes..."
          @input="handleSearch"
          @keyup.enter="performSearch"
        />
      </div>
      <button class="create-note-btn" @click="showCreateNoteModal">
        + Create Note
      </button>
    </div>
    
    <!-- 标签切换栏 -->
    <div class="tabs-section">
      <div 
        class="tab-item"
        :class="{ active: activeTab === 'all' }"
        @click="setActiveTab('all')"
      >
        All Notes
      </div>
      <div 
        class="tab-item folders-tab"
        :class="{ active: activeTab === 'folders' }"
        @click="handleFoldersClick"
      >
        Folders
        <i class="dropdown-icon">⌄</i>
      </div>
      <div 
        class="tab-item"
        :class="{ active: activeTab === 'recorded' }"
        @click="setActiveTab('recorded')"
      >
        Recorded Files
        <i class="arrow-icon">›</i>
      </div>
    </div>
    
    <!-- 笔记列表 -->
    <div class="notes-container">
      <!-- 加载中状态 -->
      <div v-if="isLoading" class="loading-container">
        <div v-for="i in 5" :key="`skeleton-${i}`" class="note-skeleton">
          <div class="skeleton-icon"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-date"></div>
          </div>
          <div class="skeleton-menu"></div>
        </div>
      </div>
      
      <!-- 笔记列表 -->
      <div v-else class="notes-list">
        <NoteItem
          v-for="note in filteredNotes"
          :key="note.noteid"
          :note="note"
          @click="openNote"
          @showMenu="showNoteMenu"
        />
        
        <!-- 空状态 -->
        <div v-if="filteredNotes.length === 0" class="empty-state">
          <div class="empty-icon">📝</div>
          <p>No notes found</p>
          <p class="empty-subtitle">Start creating your first note!</p>
        </div>
      </div>
    </div>
    
    <!-- 悬浮新建按钮 -->
    <div class="floating-new-btn" @click="showCreateNoteModal">
      + New Note
    </div>
    
    <!-- 文件夹弹窗 -->
    <FoldersModal
      v-if="showFolderModal"
      :folders="folders"
      @close="closeFolderModal"
      @selectFolder="selectFolder"
      @manageFolders="manageFolders"
    />
    
    <!-- 新建笔记选择弹窗 -->
    <CreateNoteModal
      v-if="showCreateModal"
      @close="closeCreateModal"
      @selectOption="handleCreateOption"
    />
    
    <!-- 笔记菜单弹窗 -->
    <NoteMenuModal
      v-if="showNoteMenuModal"
      :note="selectedNote"
      @close="closeNoteMenu"
      @rename="renameNote"
      @move="moveNote"
      @delete="deleteNote"
    />
  </div>
</template>

<script>
import MobileHeader from './components/MobileHeader.vue'
import NoteItem from './components/NoteItem.vue'
import FoldersModal from './components/FoldersModal.vue'
import CreateNoteModal from './components/CreateNoteModal.vue'
import NoteMenuModal from './components/NoteMenuModal.vue'

export default {
  name: 'MobileHome',
  components: {
    MobileHeader,
    NoteItem,
    FoldersModal,
    CreateNoteModal,
    NoteMenuModal
  },
  data() {
    return {
      title: "EasyNoteAI",
      searchQuery: '',
      activeTab: 'all',
      isLoading: false,
      
      // 数据
      notes: [],
      folders: [],
      
      // 弹窗状态
      showFolderModal: false,
      showCreateModal: false,
      showNoteMenuModal: false,
      selectedNote: null,
      
      // 分页
      currentPage: 1,
      pageSize: 20,
      totalNotes: 0,
      
      // 搜索防抖
      searchTimeout: null
    }
  },
  computed: {
    filteredNotes() {
      let filtered = this.notes
      
      if (this.activeTab === 'recorded') {
        filtered = filtered.filter(note => 
          note.filetype === 'record' || note.filetype === 'audio'
        )
      }
      
      return filtered
    }
  },
  async mounted() {
    await this.initData()
  },
  methods: {
    async initData() {
      try {
        await Promise.all([
          this.loadNotes(),
          this.loadFolders()
        ])
      } catch (error) {
        console.error('初始化数据失败:', error)
      }
    },
    
    async loadNotes() {
      this.isLoading = true
      try {
        const subid = this.$cookies.get("subid")
        const response = await this.$axios.post("/api/getmynotes/", {
          subid: subid,
          currentpage: this.currentPage,
          pagesize: this.pageSize,
          searchtext: this.searchQuery,
          searchtype: 'folder',
        })
        
        if (response.data.code === 200) {
          this.notes = response.data.mynotes
          this.totalNotes = response.data.total
        }
      } catch (error) {
        console.error('获取笔记失败:', error)
        this.$message?.error('获取笔记失败')
      } finally {
        this.isLoading = false
      }
    },
    
    async loadFolders() {
      try {
        const subid = this.$cookies.get("subid")
        const response = await this.$axios.post("/api/getfolders/", {
          subid: subid
        })
        
        if (response.data.code === 200) {
          this.folders = response.data.data.folders
        }
      } catch (error) {
        console.error('获取文件夹失败:', error)
      }
    },
    
    // 标签切换
    setActiveTab(tab) {
      this.activeTab = tab
    },
    
    handleFoldersClick() {
      if (this.activeTab === 'folders') {
        this.showFolderModal = true
      } else {
        this.setActiveTab('folders')
      }
    },
    
    // 搜索相关
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.performSearch()
      }, 500)
    },
    
    async performSearch() {
      this.currentPage = 1
      await this.loadNotes()
    },
    
    // 笔记操作
    openNote(note) {
      this.$router.push({
        name: "newnotesPage",
        params: { note: note },
      })
    },
    
    showNoteMenu(note) {
      this.selectedNote = note
      this.showNoteMenuModal = true
    },
    
    closeNoteMenu() {
      this.showNoteMenuModal = false
      this.selectedNote = null
    },
    
    // 文件夹相关
    closeFolderModal() {
      this.showFolderModal = false
    },
    
    selectFolder(folder) {
      console.log('选择文件夹:', folder)
      // 实现文件夹切换逻辑
    },
    
    manageFolders() {
      console.log('管理文件夹')
      // 实现管理文件夹逻辑
    },
    
    // 创建笔记相关
    showCreateNoteModal() {
      this.showCreateModal = true
    },
    
    closeCreateModal() {
      this.showCreateModal = false
    },
    
    async handleCreateOption(option) {
      this.closeCreateModal()
      
      try {
        // 检查权限
        const subid = this.$cookies.get("subid")
        const response = await this.$axios.post('/api/uploadisok/', {
          subid: subid,
          model: option.type,
        })
        
        if (response.data.code !== 200) {
          this.$message?.error(response.data.messages)
          return
        }
        
        // 处理不同的创建选项
        switch (option.type) {
          case 'record':
            this.handleRecordAudio()
            break
          case 'import':
            this.handleImportAudio()
            break
          case 'camera':
            this.handleTakePicture()
            break
          case 'pdf':
            this.handleUploadPDF()
            break
          case 'youtube':
            this.handleYouTubeVideo()
            break
        }
      } catch (error) {
        console.error('创建笔记失败:', error)
        this.$message?.error('创建笔记失败')
      }
    },
    
    handleRecordAudio() {
      // 跳转到录音页面
      console.log('开始录音')
    },
    
    handleImportAudio() {
      // 打开文件选择
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'audio/*'
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (file) {
          this.uploadAudioFile(file)
        }
      }
      input.click()
    },
    
    handleTakePicture() {
      // 打开相机
      console.log('拍照')
    },
    
    handleUploadPDF() {
      // 打开PDF选择
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.pdf,.doc,.docx,.ppt,.pptx,.txt'
      input.onchange = (e) => {
        const file = e.target.files[0]
        if (file) {
          this.uploadDocumentFile(file)
        }
      }
      input.click()
    },
    
    handleYouTubeVideo() {
      // 实现YouTube视频处理
      console.log('YouTube视频')
    },
    
    async uploadAudioFile(file) {
      // 实现音频文件上传
      console.log('上传音频文件:', file)
    },
    
    async uploadDocumentFile(file) {
      // 实现文档文件上传
      console.log('上传文档文件:', file)
    },
    
    // 笔记菜单操作
    renameNote(note) {
      console.log('重命名笔记:', note)
    },
    
    moveNote(note) {
      console.log('移动笔记:', note)
    },
    
    deleteNote(note) {
      console.log('删除笔记:', note)
    },
    
    // 其他
    handleShowSettings() {
      console.log('显示设置')
    }
  }
}
</script>

<style lang="less" scoped>
@import './styles/mobile-common.less';

.mobile-home {
  width: 100%;
  min-height: 100vh;
  background-color: var(--bg-secondary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

// 搜索和创建按钮区域
.search-create-section {
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  background: white;
  
  .search-wrapper {
    flex: 1;
    position: relative;
    
    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 16px;
    }
    
    input {
      width: 100%;
      height: 44px;
      padding-left: 40px;
      padding-right: 12px;
      border: none;
      border-radius: 22px;
      background-color: #f1f3f4;
      font-size: 16px;
      outline: none;
      
      &::placeholder {
        color: #999;
      }
    }
  }
  
  .create-note-btn {
    height: 44px;
    padding: 0 20px;
    background: #5065f6;
    color: white;
    border: none;
    border-radius: 22px;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    
    &:active {
      background: #3d52cc;
    }
  }
}

// 标签栏
.tabs-section {
  display: flex;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  
  .tab-item {
    flex: 1;
    padding: 16px 8px;
    text-align: center;
    font-size: 16px;
    color: #6b7280;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    
    &.active {
      color: #5065f6;
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: #5065f6;
        border-radius: 2px 2px 0 0;
      }
    }
    
    .dropdown-icon,
    .arrow-icon {
      font-size: 14px;
      margin-left: 4px;
    }
  }
}

// 笔记容器
.notes-container {
  flex: 1;
  padding: 16px;
}

// 加载骨架屏
.loading-container {
  .note-skeleton {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 8px;
    background: white;
    border-radius: 12px;
    
    .skeleton-icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      background: #e5e7eb;
      margin-right: 12px;
    }
    
    .skeleton-content {
      flex: 1;
      
      .skeleton-title {
        height: 16px;
        background: #e5e7eb;
        border-radius: 4px;
        margin-bottom: 8px;
      }
      
      .skeleton-date {
        height: 12px;
        width: 60%;
        background: #f3f4f6;
        border-radius: 4px;
      }
    }
    
    .skeleton-menu {
      width: 24px;
      height: 24px;
      background: #e5e7eb;
      border-radius: 4px;
    }
  }
}

// 空状态
.empty-state {
  text-align: center;
  padding: 60px 20px;
  
  .empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
  }
  
  p {
    margin: 8px 0;
    font-size: 18px;
    color: #374151;
  }
  
  .empty-subtitle {
    font-size: 14px;
    color: #9ca3af;
  }
}

// 悬浮新建按钮
.floating-new-btn {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: #5065f6;
  color: white;
  padding: 16px 32px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 4px 20px rgba(80, 101, 246, 0.4);
  cursor: pointer;
  z-index: 100;
  
  &:active {
    transform: translateX(-50%) scale(0.95);
  }
}

// 响应式适配
@media (max-width: 375px) {
  .search-create-section {
    padding: 12px;
    gap: 8px;
    
    .create-note-btn {
      padding: 0 16px;
      font-size: 14px;
    }
  }
  
  .tabs-section .tab-item {
    padding: 12px 4px;
    font-size: 14px;
  }
  
  .floating-new-btn {
    bottom: 80px;
    padding: 12px 24px;
    font-size: 14px;
  }
}
</style>